/**
 * Height-adaptive sizing helpers for the `record_list` dashboard widget.
 *
 * When a `record_list` widget omits `limit` it sizes its fetched/rendered row
 * count to the space actually available (see {@link RecordListWidget}). The
 * naive implementation measured the widget's own wrapper element via
 * `getBoundingClientRect().height` and divided by the row height. That is unsafe:
 * if the CSS percentage-height chain fails to resolve (a wrapper with
 * `height: 100%` whose ancestor has no *definite* height grows to its CONTENT
 * height), the measured height becomes the full rendered table height. Feeding
 * that back into the fetch count creates a measure → fetch-more → grow-taller
 * → measure-larger positive-feedback loop that runs the fetch up to thousands of
 * rows and trips 60s API timeouts.
 *
 * These helpers make the sizing robust and self-contained:
 *  - {@link measuredAvailableHeight} reads the available height from the nearest
 *    *bounded* scroll-clipping ancestor's `clientHeight` (which never exceeds its
 *    allocated box), instead of a wrapper that can grow with its content.
 *  - {@link computeAdaptiveRowCount} hard-caps the result at {@link MAX_ADAPTIVE_ROWS}
 *    as an unconditional backstop, so even a mis-measurement can never request an
 *    unbounded row count.
 */

/**
 * Absolute upper bound on the number of rows a height-adaptive `record_list`
 * widget will ever fetch/render. A dashboard widget cell is at most viewport-tall
 * (~30px/row → a few dozen rows), so 100 is a generous backstop that still makes
 * a runaway fetch (hundreds–thousands of rows → 60s timeouts) impossible.
 */
export const MAX_ADAPTIVE_ROWS = 100;

/**
 * Pure computation of how many whole rows fit the available height.
 *
 * @param availableHeight visible height available to the list body, in px
 * @param topChrome height of the toolbar + table header above the first data row, in px
 * @param rowHeight measured height of a single data row, in px
 * @param maxRows hard upper bound (defaults to {@link MAX_ADAPTIVE_ROWS})
 * @param bottomChrome height of chrome BELOW the rows that shares the same
 *        clipping box — in adaptive mode the server-side pagination footer
 *        (`MuiTablePagination`, ~52px). Subtracting it prevents the footer from
 *        pushing the last row past the panel and forcing an internal scrollbar.
 *        Defaults to `0` (classic mode / no footer).
 * @returns an integer in `[1, maxRows]`, or `null` when inputs are not measurable
 *          yet (non-positive height/row) so the caller can leave the current value.
 */
export const computeAdaptiveRowCount = (
    availableHeight: number,
    topChrome: number,
    rowHeight: number,
    maxRows: number = MAX_ADAPTIVE_ROWS,
    bottomChrome = 0,
): number | null => {
    if (
        !Number.isFinite(availableHeight) ||
        !Number.isFinite(rowHeight) ||
        rowHeight <= 0 ||
        availableHeight <= 0
    ) {
        return null;
    }
    const usable =
        availableHeight - Math.max(0, topChrome) - Math.max(0, bottomChrome);
    const raw = Math.floor(usable / rowHeight);
    // At least one row (a too-tall row still shows one, overflow-scrolled), never
    // more than the hard cap.
    return Math.min(maxRows, Math.max(1, raw));
};

/**
 * Snap a page-start `offset` to a valid page boundary for a paginated list,
 * clamping it into range when the total row count shrinks below the current page
 * (e.g. records were deleted while the widget showed a later page).
 *
 * @param offset the requested page-start offset
 * @param pageSize rows per page (the height-derived adaptive page size)
 * @param totalCount total rows available on the server
 * @returns a page-aligned offset in `[0, lastPageStart]`; `0` when inputs are not
 *          yet measurable or the list is empty.
 */
export const clampPageOffset = (
    offset: number,
    pageSize: number,
    totalCount: number,
): number => {
    if (!Number.isFinite(offset) || offset <= 0) {
        return 0;
    }
    if (!Number.isFinite(pageSize) || pageSize <= 0) {
        return 0;
    }
    if (!Number.isFinite(totalCount) || totalCount <= 0) {
        return 0;
    }
    if (offset >= totalCount) {
        // Snap to the first row of the last page rather than jumping to page 0.
        return Math.max(0, Math.floor((totalCount - 1) / pageSize) * pageSize);
    }
    return offset;
};

/**
 * Extract the rows (and, for the server-paginated wrapper, the total count) from a
 * dashboard `record_list` GraphQL response, for either query shape the widget
 * issues:
 *  - height-adaptive (server pagination): `<type>_list` returns a wrapper
 *    `{ <type>_count: Int, <type>_list: [<type>] }`;
 *  - classic (client pagination): `<type>_plain_list` returns a bare array.
 *
 * @param responseData the GraphQL response `data` object
 * @param apiType the lower-cased record type (e.g. `custom_object`)
 * @param heightAdaptive whether the server-paginated wrapper shape was requested
 * @returns the rows and, in adaptive mode, the server total (`null` otherwise).
 */
export const parseRecordListResponse = (
    responseData: Record<string, any> | null | undefined,
    apiType: string,
    heightAdaptive: boolean,
): { rows: any[]; totalCount: number | null } => {
    if (!responseData || !apiType) {
        return { rows: [], totalCount: null };
    }
    if (heightAdaptive) {
        const wrapper = responseData[`${apiType}_list`];
        const rows = wrapper?.[`${apiType}_list`];
        const count = wrapper?.[`${apiType}_count`];
        return {
            rows: Array.isArray(rows) ? rows : [],
            totalCount: Number.isFinite(count) ? count : null,
        };
    }
    const rows = responseData[`${apiType}_plain_list`];
    return { rows: Array.isArray(rows) ? rows : [], totalCount: null };
};

/**
 * Clamp the rendered rows of a `record_list` widget to the current adaptive page
 * size, enforcing the display-safety invariant "rows shown never exceed the page
 * size the paginator advertises".
 *
 * The height-adaptive widget fetches exactly `pageSize` rows per page, so in
 * steady state this is a no-op. It only trims a transient/stale over-fetch — rows
 * left over from a briefly-larger pre-settle page size whose `limit = pageSize`
 * refetch has not yet replaced them — which would otherwise render an extra row
 * under a smaller-page paginator (label/rows mismatch) and push the last row plus
 * the pagination footer past the panel into an internal scrollbar.
 *
 * Classic mode (not height-adaptive, or before the page size is measured) is
 * returned unchanged, so its client-side pagination over a single limited fetch
 * cannot regress.
 *
 * @param rows the fetched rows
 * @param heightAdaptive whether the widget is in height-adaptive (server-paginated) mode
 * @param pageSize the current height-derived page size (`null` while still measuring)
 * @returns `rows` unchanged in classic/unmeasured mode, else `rows` sliced to at most `pageSize`
 */
export const clampAdaptiveDisplayRows = <T>(
    rows: T[],
    heightAdaptive: boolean,
    pageSize: number | null,
): T[] => {
    if (!heightAdaptive || pageSize == null || !Number.isFinite(pageSize)) {
        return rows;
    }
    if (pageSize < 0 || rows.length <= pageSize) {
        return rows;
    }
    return rows.slice(0, pageSize);
};

/**
 * The height genuinely available to lay out rows: the smallest `clientHeight`
 * among `el` and its scroll-clipping ancestors. A clipping box (overflow-y
 * auto/scroll/hidden) never renders taller than its allocated space, so its
 * `clientHeight` is a trustworthy bound — unlike a `height: 100%` wrapper that
 * can grow to its content height when the percentage chain fails to resolve.
 *
 * Walks up at most `maxHops` ancestors and stops at the `.DashboardWidget` cell
 * (inclusive), the outermost box that clips to the grid cell. Returns
 * `el.clientHeight` when no clipping ancestor is found.
 */
export const measuredAvailableHeight = (
    el: HTMLElement,
    maxHops = 12,
): number => {
    let bound = Infinity;
    let node: HTMLElement | null = el;
    for (let hops = 0; node && hops <= maxHops; hops++) {
        const isSelf = node === el;
        const style = window.getComputedStyle(node);
        const clips =
            style.overflowY === "auto" ||
            style.overflowY === "scroll" ||
            style.overflowY === "hidden";
        if ((isSelf || clips) && node.clientHeight > 0) {
            bound = Math.min(bound, node.clientHeight);
        }
        if (node.classList.contains("DashboardWidget")) {
            break;
        }
        node = node.parentElement;
    }
    return Number.isFinite(bound) ? bound : el.clientHeight;
};
